import { useState } from "react";
import type { CollapseProps } from "antd";
import { useTranslation } from "react-i18next";

import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import { AccordionVariant } from "@/components_v2/Accordion/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TARGETS,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import ExternalLink from "@/assets/icons/external-link.svg?react";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import EvidencePreviewPanel from "./EvidencePreviewPanel";
import type { EvidencePreviewAccordionProps } from "../types";

import "../styles.css";

const EvidencePreviewAccordion = ({
  blocks,
}: EvidencePreviewAccordionProps) => {
  const { t } = useTranslation();
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const handleChange: CollapseProps["onChange"] = (keys) => {
    setActiveKeys(
      Array.isArray(keys) ? keys.map(String) : keys ? [String(keys)] : [],
    );
  };

  if (blocks.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-200">
      <p className="body-m-regular">
        {t("survey.labels.attachedEvidence")} ({blocks.length})
      </p>

      <BaseAccordion
        variant={AccordionVariant.FILLED}
        activeKey={activeKeys}
        onChange={handleChange}
        expandIconPosition="end"
        className="evidence-preview-accordion flex flex-col gap-200"
        items={blocks.map((block) => {
          const blockKey = String(block.id);

          return {
            key: blockKey,
            label: (
              <span className="evidence-preview-accordion-label flex items-center gap-200 full-width">
                <span className="flex items-center gap-100 body-m-medium">
                  {getEvidenceTitle(block) ?? ""}
                </span>

                <BaseButton
                  size={BUTTON_SIZES.EXTRA_SMALL}
                  variant={BUTTON_VARIANTS.LINK}
                  type={BUTTON_TYPES.LINK}
                  aria-label={t("datablocks.labels.seeDataBlock")}
                  to={`/evidence/${block.id}`}
                  target={BUTTON_TARGETS.BLANK}
                >
                  <BaseSvgRenderer
                    component={ExternalLink}
                    width="20"
                    height="20"
                  />
                </BaseButton>
              </span>
            ),
            children: activeKeys.includes(blockKey) ? (
              <EvidencePreviewPanel blockId={block.id} />
            ) : null,
          };
        })}
      />
    </div>
  );
};

export default EvidencePreviewAccordion;
